import { useEffect, useRef, useState } from 'react'
import { ChevronDown, LogOut, Shield } from 'lucide-react'

export interface UserMenuProps {
  user?: { name: string; avatarUrl?: string }
  onLogout?: () => void
  /** When set (admins only), shows an "Admin" item that opens the admin area. */
  onAdmin?: () => void
}

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/**
 * Top-right user menu: avatar (initials or photo), name on desktop,
 * and a lightweight dropdown with the signed-in identity and Log out.
 * Hand-rolled (no UI lib) so it is portable to any React codebase.
 */
export function UserMenu({ user = { name: 'DSD Finance' }, onLogout, onAdmin }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-1.5 transition-colors hover:bg-stone-100 dark:hover:bg-stone-800"
      >
        <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-champagne-100 text-xs font-semibold text-champagne-800 dark:bg-champagne-500/20 dark:text-champagne-300">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            initials(user.name)
          )}
        </span>
        <span className="hidden text-sm font-medium text-stone-700 md:block dark:text-stone-200">
          {user.name}
        </span>
        <ChevronDown className="hidden h-4 w-4 text-stone-400 md:block" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-lg dark:border-stone-800 dark:bg-stone-900"
        >
          <div className="px-3 py-2 text-xs text-stone-400">
            Signed in as
            <span className="mt-0.5 block font-medium text-stone-700 dark:text-stone-200">
              {user.name}
            </span>
          </div>
          <div className="my-1 h-px bg-stone-100 dark:bg-stone-800" />
          {onAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onAdmin()
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-stone-800"
            >
              <Shield className="h-4 w-4" />
              Admin
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onLogout?.()
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-stone-50 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  )
}

export default UserMenu
